import { Link, useNavigate, useLocation } from "react-router-dom";
import { ShoppingCart, Plus } from "lucide-react";
import { fileUrl, fmtIQD } from "../lib/api";
import { useCart } from "../lib/cart";
import { toast } from "sonner";
import ProductImage from "./ProductImage";

export default function ProductCard({ product }) {
  const { add } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const p = product;
  const strikeOriginal = (p.previous_price && p.previous_price > p.final_price)
    ? p.previous_price
    : (p.base_price && Number(p.base_price) > Number(p.final_price) ? p.base_price : null);
  const hasDiscount = p.discount_percent > 0 || !!strikeOriginal;
  const isNew = p.badge === "new";
  const orderingEnabled = p.ordering_enabled !== false;
  const totalStock = (p.variants?.reduce((s, v) => s + (v.stock || 0), 0)) || (p.variants?.length === 0 ? 1 : 0);
  const outOfStock = totalStock <= 0;
  const unavailable = !orderingEnabled || outOfStock;
  const canQuickAdd = !unavailable && ((p.variants?.length === 1 && p.variants[0].stock > 0) || (!p.variants?.length));

  const quickAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const v = p.variants?.[0] || null;
    const ok = add(p, v, 1);
    if (!ok) {
      toast.error("يرجى تسجيل الدخول أو إنشاء حساب لإضافة المنتجات إلى السلة");
      const returnTo = encodeURIComponent(location.pathname + location.search);
      navigate(`/login?returnTo=${returnTo}`);
      return;
    }
    toast.success("تمت الإضافة إلى السلة");
  };

  return (
    <Link
      to={`/product/${p.id}`}
      data-testid={`product-card-${p.id}`}
      className="group relative flex flex-col bg-[#24262B] border border-[#3A3D43] rounded-lg overflow-hidden pg-card-hover"
    >
      {/* Image — locked 2584:1516 ratio, object-cover */}
      <div className="relative">
        <ProductImage src={p.cover_image} alt={p.name} size="card" className="bg-[#191A1D] transition-transform duration-500 group-hover:scale-[1.03]" />
        {/* Badges */}
        <div className="absolute top-3 right-3 flex flex-col gap-1 z-10">
          {hasDiscount && (
            <span data-testid={`discount-badge-${p.id}`} className="bg-[#EF1B17] text-white text-xs font-black px-2 py-1 rounded">
              -{p.discount_percent}%
            </span>
          )}
          {isNew && !hasDiscount && (
            <span className="bg-[#F4F4F2] text-[#191A1D] text-xs font-black px-2 py-1 rounded">جديد</span>
          )}
          {p.badge === "best_seller" && (
            <span className="bg-[#191A1D] border border-[#EF1B17] text-[#EF1B17] text-xs font-black px-2 py-1 rounded">الأكثر مبيعاً</span>
          )}
          {unavailable && (
            <span data-testid={`unavailable-badge-${p.id}`} className="bg-gray-800/95 border border-gray-500 text-gray-200 text-xs font-black px-2 py-1 rounded">
              غير متوفر حالياً
            </span>
          )}
        </div>
        {/* Quick add */}
        {canQuickAdd && (
          <button
            data-testid={`quick-add-${p.id}`}
            onClick={quickAdd}
            className="absolute bottom-2 left-2 right-2 z-10 flex items-center justify-center gap-2 py-1.5 rounded-md bg-[#191A1D]/85 backdrop-blur border border-[#3A3D43] text-xs font-bold text-[#F4F4F2] opacity-0 group-hover:opacity-100 transition-opacity hover:bg-[#EF1B17] hover:border-[#EF1B17]"
          >
            <Plus size={14} /> إضافة سريعة
          </button>
        )}
      </div>
      {/* Body */}
      <div className="p-3 flex flex-col gap-1.5 flex-1">
        {p.agency_name && (
          <div className="text-[10px] font-bold uppercase tracking-widest text-[#EF1B17]">{p.agency_name}</div>
        )}
        <h3 className="text-sm font-bold text-[#F4F4F2] line-clamp-2 min-h-[2.5rem]">{p.name}</h3>
        <div className="flex items-baseline justify-between mt-auto pt-1">
          <div>
            <div className="text-lg font-black text-[#F4F4F2]">{fmtIQD(p.final_price)}</div>
            {hasDiscount && strikeOriginal && (
              <div className="text-xs text-gray-500 line-through" data-testid={`old-price-${p.id}`}>{fmtIQD(strikeOriginal)}</div>
            )}
            {p.discount_source === "campaign" && p.discount_title && (
              <div className="text-[10px] text-[#EF1B17] font-bold mt-0.5" data-testid={`campaign-title-${p.id}`}>{p.discount_title}</div>
            )}
          </div>
          <span className="text-xs font-bold text-[#EF1B17] group-hover:text-[#FF3B30] transition-colors">عرض ←</span>
        </div>
      </div>
    </Link>
  );
}

export function ProductSkeleton() {
  return (
    <div className="rounded-lg overflow-hidden border border-[#3A3D43]">
      <div className="pg-skeleton" style={{ aspectRatio: "2584 / 1516" }} />
      <div className="p-3 space-y-2">
        <div className="h-3 w-1/3 pg-skeleton rounded" />
        <div className="h-4 w-full pg-skeleton rounded" />
        <div className="h-5 w-1/2 pg-skeleton rounded" />
      </div>
    </div>
  );
}
